import apiClient from '../utils/apiClient';

const getApiErrorMessage = (error, fallbackMessage) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage
  );
};

export const integrationService = {
  async createIntegration(integrationData) {
    try {
      if (!integrationData.project_id) {
        throw new Error('project_id is required to create an integration');
      }

      const response = await apiClient.post('/api/integrations', integrationData);
      return response.data;
    } catch (error) {
      console.error('Error creating integration:', error);
      throw new Error(getApiErrorMessage(error, 'Failed to create integration'));
    }
  },

  async listIntegrations(projectId) {
    try {
      if (!projectId) {
        throw new Error('project_id is required to list integrations');
      }

      const response = await apiClient.get('/api/integrations', { projectId });
      return response.data;
    } catch (error) {
      console.error('Error listing integrations:', error);
      throw new Error(getApiErrorMessage(error, 'Failed to load integrations'));
    }
  },

  async deleteIntegration(id) {
    try {
      await apiClient.delete(`/api/integrations/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting integration:', error);
      throw new Error(getApiErrorMessage(error, 'Failed to delete integration'));
    }
  },

  async updateIntegration(id, updateData) {
    try {
      const response = await apiClient.patch(`/api/integrations/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating integration:', error);
      throw new Error(getApiErrorMessage(error, 'Failed to update integration'));
    }
  },

  // WordPress specific methods
  async verifyWordPressCredentials(credentials) {
    try {
      const response = await apiClient.post('/api/integrations/wordpress/verify', credentials);
      return response.data;
    } catch (error) {
      console.error('Error verifying WordPress credentials:', error);
      throw new Error(error.response?.data?.error || 'Failed to verify WordPress credentials');
    }
  },

  async publishToWordPress(data) {
    try {
      const response = await apiClient.post('/api/integrations/wordpress/publish', data);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to publish to WordPress');
      }

      return response.data;
    } catch (error) {
      console.error('Error publishing to WordPress:', error);
      throw new Error(error.response?.data?.error || 'Failed to publish to WordPress');
    }
  },

  // Webflow specific methods
  async verifyWebflowCredentials(credentials) {
    try {
      const response = await apiClient.post('/api/integrations/webflow/verify', credentials);
      return response.data;
    } catch (error) {
      console.error('Error verifying Webflow credentials:', error);
      throw error;
    }
  },

  async getWebflowCollections(data) {
    try {
      const response = await apiClient.post('/api/integrations/webflow/collections', data);
      return response.data;
    } catch (error) {
      console.error('Error fetching Webflow collections:', error);
      throw error;
    }
  },

  async publishToWebflow(data) {
    try {
      const response = await apiClient.post('/api/integrations/webflow/publish', data);
      return response.data;
    } catch (error) {
      console.error('Error publishing to Webflow:', error);
      throw error;
    }
  },

  // Notion specific methods
  async getNotionAuthUrl(redirectUri) {
    try {
      const response = await apiClient.post('/api/integrations/notion/auth-url', { redirect_uri: redirectUri });
      return response.data;
    } catch (error) {
      console.error('Error getting Notion auth URL:', error);
      throw error;
    }
  },

  async completeNotionAuth(code, redirectUri) {
    try {
      const response = await apiClient.post('/api/integrations/notion/complete-auth', {
        code,
        redirect_uri: redirectUri
      });
      return response.data;
    } catch (error) {
      console.error('Error completing Notion authentication:', error);
      throw error;
    }
  },

  async getNotionDatabases(accessToken) {
    try {
      const response = await apiClient.post('/api/integrations/notion/databases', { access_token: accessToken });
      return response.data;
    } catch (error) {
      console.error('Error fetching Notion databases:', error);
      throw error;
    }
  },

  async publishToNotion(data) {
    try {
      const response = await apiClient.post('/api/integrations/notion/publish', data);
      return response.data;
    } catch (error) {
      console.error('Error publishing to Notion:', error);
      throw error;
    }
  },

  // Shopify specific methods
  async verifyShopifyCredentials(credentials) {
    try {
      const response = await apiClient.post('/api/integrations/shopify/verify', credentials);
      return response.data;
    } catch (error) {
      console.error('Error verifying Shopify credentials:', error);
      throw new Error(error.response?.data?.error || 'Failed to verify Shopify credentials');
    }
  },

  async getShopifyBlogs(credentials) {
    try {
      const response = await apiClient.post('/api/integrations/shopify/blogs', credentials);
      return response.data;
    } catch (error) {
      console.error('Error fetching Shopify blogs:', error);
      throw error;
    }
  },

  async publishToShopify(data) {
    try {
      const response = await apiClient.post('/api/integrations/shopify/publish', data);
      return response.data;
    } catch (error) {
      console.error('Error publishing to Shopify:', error);
      throw new Error(error.response?.data?.error || 'Failed to publish to Shopify');
    }
  },

  // Wix specific methods
  async verifyWixCredentials(credentials) {
    try {
      const response = await apiClient.post('/api/integrations/wix/verify', credentials);
      return response.data;
    } catch (error) {
      console.error('Error verifying Wix credentials:', error);
      throw new Error(error.response?.data?.error || 'Failed to verify Wix credentials');
    }
  },

  async getWixBlogs(credentials) {
    try {
      const response = await apiClient.post('/api/integrations/wix/blogs', credentials);
      return response.data;
    } catch (error) {
      console.error('Error fetching Wix blogs:', error);
      throw error;
    }
  },

  async publishToWix(data) {
    try {
      const response = await apiClient.post('/api/integrations/wix/publish', data);
      return response.data;
    } catch (error) {
      console.error('Error publishing to Wix:', error);
      throw new Error(error.response?.data?.error || 'Failed to publish to Wix');
    }
  }
}; 
